import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AddUserToPodFormData, CronSchedulerFields, EndpointFormData, ModelModelType, OnboardingRequestItem, PodItem, PodOnboardingRequestBody, RequestDetails, RoleItem, TrainingFormData } from "utility/types";

export type onboardingInitialStates = {
  isSubmitingUserOnboardForm: boolean;
  isUserOnboardFormSubmited: boolean;
  podList: Array<PodItem>;
  roles: Array<RoleItem>;
  isLoadingUserOnboardingFormInitialData: boolean;
  isUserOnboarded: boolean;
  requests: Array<OnboardingRequestItem>;
  isLoadingRequestDetails: boolean;
  isLoadingRequestStatus: boolean;
  requestDetails: RequestDetails | null;
  isSubmitingPodOnboardForm: boolean;
  isPodOnboardFormSubmited: boolean;
  batchInferencingStep1Data: CronSchedulerFields;
  endpointCreationStep1Data: EndpointFormData;
  trainingStep1Data: TrainingFormData;
  // Pagination state
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
  // Filter state
  filters: {
    status: string;
    onboardingType: string;
    requestNumber: string;
    raisedBy: string;
    raisedFor: string;
  };
  // Sort state
  sortBy: string;
  sortOrder: "asc" | "desc";
  isLoadingRequests: boolean;
};

export const initialEnpointCreationData = {
  // General
  pod_name: "",
  author: "",
  team_name: "",
  team_email_address: "",
  project_name: "",

  // Model Configuration
  model_mode: ModelModelType.SingleModel,
  modelConfigurations: [],

  // Transform job
  variant_name: "",
  instance_count: 1,
  instance_type: "",

  // github config
  github_user_id: "",
  pat_token: "",
  template_creation_option: "pushToGithub"
}

export const initialTrainingData = {
  // General
  pod_name: "",
  author: "",
  team_name: "",
  team_email_address: "",
  project_name: "",

  // github config
  github_user_id: "",
  pat_token: "",
  template_creation_option: "pushToGithub",

  // Compute resource
  instance_count: 1,
  instance_type: "",
  volume_size: 32,

  // Model configuration
  max_retries: 1,
  train_comp_type: "",
  train_dist_type: "",
  train_s3_uri: "",
  validation_comp_type: "",
  validation_dist_type: "",
  validation_s3_uri: "",
  test_comp_type: "",
  test_dist_type: "",
  test_s3_uri: "",

  // W&B config
  wandb_project_name: "",
  model_name_custom: "",
  wandb_model_version: "",

  enable_schedule: "false"
}

export const initialBatchInferencingData = {
  // General
  pod_name: "",
  author: "",
  team_name: "",
  team_email_address: "",
  project_name: "",

  // Model Configuration
  model_mode: ModelModelType.SingleModel,
  modelConfigurations: [],

  // Transform job
  batch_strategy: "SingleRecord",
  input_type: "text/csv",
  input_inference_path: "",
  output_inference_path: "",
  max_payload_inmb: 1,
  max_concurrent_transforms: 1,
  instance_count: 1,
  instance_type: "",

  // Scheduling
  enable_schedule: "false",
  minute: "",
  hour: "",
  dayOfMonth: "",
  month: "",
  dayOfWeek: "",
  year: "",

  // github config
  github_user_id: "",
  pat_token: "",
  template_creation_option: "pushToGithub"
}

const initialState: onboardingInitialStates = {
  isSubmitingUserOnboardForm: false,
  isUserOnboardFormSubmited: false,
  isSubmitingPodOnboardForm: false,
  isPodOnboardFormSubmited: false,
  isLoadingUserOnboardingFormInitialData: false,
  podList: [],
  roles: [],
  isUserOnboarded: false,
  requests: [],
  isLoadingRequestDetails: false,
  requestDetails: null,
  isLoadingRequestStatus: false,
  batchInferencingStep1Data: initialBatchInferencingData,
  endpointCreationStep1Data: initialEnpointCreationData,
  trainingStep1Data: initialTrainingData,
  // Pagination initial state
  totalCount: 0,
  currentPage: 1,
  pageSize: 20,
  totalPages: 0,
  hasNext: false,
  hasPrevious: false,
  // Filter initial state
  filters: {
    status: "",
    onboardingType: "",
    requestNumber: "",
    raisedBy: "",
    raisedFor: "",
  },
  // Sort initial state
  sortBy: "created_at",
  sortOrder: "desc",
  isLoadingRequests: false,
};

const sagaReducers = {
  callToSubmitUserOnboardForm: (state: onboardingInitialStates, action: PayloadAction<AddUserToPodFormData>) => {},
  callToLoadUserOnboardingFormInitialData: () => {},
  callToLoadPodOnboardingFormInitialData: () => {},
  callToGetRequests: (state: onboardingInitialStates, action: PayloadAction<{ email: string }>) => {},
  callToGetRequestsPaginated: (state: onboardingInitialStates, action: PayloadAction<{ email: string }>) => {},
  callToGetRequestDetails: (state: onboardingInitialStates, action: PayloadAction<{ requestId: string, isRefreshStatusFlow?: boolean, isSilentRefresh?: boolean }>) => {},
  callToSubmitPodOnboarding: (state: onboardingInitialStates, action: PayloadAction<PodOnboardingRequestBody>) => {},
};

export const userDataSlice = createSlice({
  name: "onboarding",
  initialState,
  reducers: {
    ...sagaReducers,
    setIsSubmitingUserOnboardForm: (state, action: PayloadAction<boolean>) => {
      state.isSubmitingUserOnboardForm = action.payload;
    },
    setIsUserOnboardFormSubmited: (state, action: PayloadAction<boolean>) => {
      state.isUserOnboardFormSubmited = action.payload;
    },
    setPodList: (state, action: PayloadAction<Array<PodItem>>) => {
      state.podList = action.payload;
    },
    setRoles: (state, action: PayloadAction<Array<PodItem>>) => {
      state.roles = action.payload;
    },
    setIsLoadingUserOnboardingFormInitialData: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isLoadingUserOnboardingFormInitialData = action.payload;
    },
    setIsUserOnboarded: (state, action: PayloadAction<boolean>) => {
      state.isUserOnboarded = action.payload;
    },
    setRequests: (state, action: PayloadAction<Array<OnboardingRequestItem>>) => {
      state.requests = action.payload;
    },
    setIsLoadingRequestDetails: (state, action: PayloadAction<boolean>) => {
      state.isLoadingRequestDetails = action.payload;
    },
    setRequestDetails: (state, action: PayloadAction<RequestDetails | null>) => {
      state.requestDetails = action.payload;
    },
    setIsSubmitingPodOnboardForm: (state, action: PayloadAction<boolean>) => {
      state.isSubmitingPodOnboardForm = action.payload;
    },
    setIsPodOnboardFormSubmited: (state, action: PayloadAction<boolean>) => {
      state.isPodOnboardFormSubmited = action.payload;
    },
    setIsLoadingRequestStatus: (state, action: PayloadAction<boolean>) => {
      state.isLoadingRequestStatus = action.payload;
    },
    setBatchInferencingStep1Data: (state, action: PayloadAction<CronSchedulerFields>) => {
      state.batchInferencingStep1Data = action.payload;
    },
    setEndpointCreationStep1Data: (state, action: PayloadAction<EndpointFormData>) => {
      state.endpointCreationStep1Data = action.payload;
    },
    setTrainingStep1Data: (state, action: PayloadAction<TrainingFormData>) => {
      state.trainingStep1Data = action.payload;
    },
    setPaginationData: (state, action: PayloadAction<{
      total_count: number;
      page: number;
      page_size: number;
      total_pages: number;
      has_next: boolean;
      has_previous: boolean;
    }>) => {
      state.totalCount = action.payload.total_count;
      state.currentPage = action.payload.page;
      state.pageSize = action.payload.page_size;
      state.totalPages = action.payload.total_pages;
      state.hasNext = action.payload.has_next;
      state.hasPrevious = action.payload.has_previous;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1; // Reset to first page when page size changes
    },
    // Filter actions
    setFilters: (state, action: PayloadAction<{
      status?: string;
      onboardingType?: string;
      requestNumber?: string;
      raisedBy?: string;
      raisedFor?: string;
    }>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.currentPage = 1; // Reset to first page when filters change
    },
    clearFilters: (state) => {
      state.filters = {
        status: "",
        onboardingType: "",
        requestNumber: "",
        raisedBy: "",
        raisedFor: "",
      };
      state.currentPage = 1;
    },
    // Sort actions
    setSortBy: (state, action: PayloadAction<string>) => {
      if (state.sortBy === action.payload) {
        state.sortOrder = state.sortOrder === "asc" ? "desc" : "asc";
      } else {
        state.sortBy = action.payload;
        state.sortOrder = "desc"; // Default to descending for new column
      }
      state.currentPage = 1; // Reset to first page when sort changes
    },
    setSortOrder: (state, action: PayloadAction<"asc" | "desc">) => {
      state.sortOrder = action.payload;
    },
    setIsLoadingRequests: (state, action: PayloadAction<boolean>) => {
      state.isLoadingRequests = action.payload;
    },
  },
});

export const {
  setIsSubmitingUserOnboardForm,
  setIsUserOnboardFormSubmited,
  callToSubmitUserOnboardForm,
  callToLoadUserOnboardingFormInitialData,
  setPodList,
  setRoles,
  setIsLoadingUserOnboardingFormInitialData,
  setIsUserOnboarded,
  callToGetRequests,
  callToGetRequestsPaginated,
  setRequests,
  callToLoadPodOnboardingFormInitialData,
  callToGetRequestDetails,
  setRequestDetails,
  setIsLoadingRequestDetails,
  setIsPodOnboardFormSubmited,
  setIsSubmitingPodOnboardForm,
  callToSubmitPodOnboarding,
  setIsLoadingRequestStatus,
  setBatchInferencingStep1Data,
  setEndpointCreationStep1Data,
  setTrainingStep1Data,
  setPaginationData,
  setCurrentPage,
  setPageSize,
  setFilters,
  clearFilters,
  setSortBy,
  setSortOrder,
  setIsLoadingRequests,
} = userDataSlice.actions;
export default userDataSlice.reducer;
